// @todo enable the following disabled rules see OPENTOK-31136 for more info
/* eslint-disable no-param-reassign, global-require, no-void, no-use-before-define */

const logging = require('../../../ot/logging.js');

const streamHasEnabledVideoTrack = (stream) => {
  const videoTrack = stream.getVideoTracks()[0];
  return Boolean(videoTrack && videoTrack.enabled);
};

module.exports = function streamBindingFactory(deps = {}) {
  const errors = deps.Errors || require('../../../ot/Errors.js');
  const otError = deps.otError || require('../../otError.js')();
  const videoElementErrorMap = deps.videoElementErrorMap || require('../videoElementErrorMap.js')();
  const windowMock = deps.global || global;
  const env = deps.env || require('../../../helpers/env.js');
  const POLL_INTERVAL = deps.POLL_INTERVAL || 100;
  const TIMEOUT = deps.TIMEOUT || 3000;

  const streamBinding = {};

  streamBinding.unbind = function (videoElement) {
    if (typeof videoElement.pause === 'function') {
      videoElement.pause();
    }

    videoElement.onended = null;

    if (videoElement.srcObject !== void 0) {
      videoElement.srcObject = null;
    } else if (videoElement.mozSrcObject !== void 0) {
      videoElement.mozSrcObject = null;
    } else {
      windowMock.URL.revokeObjectURL(videoElement.src);
    }
  };

  function uncheckedBind(videoElement, webRtcStream) {
    // The official spec way is 'srcObject', we are slowly converging there.
    if (videoElement.srcObject !== void 0) {
      if (videoElement.srcObject !== webRtcStream) {
        videoElement.srcObject = webRtcStream;
      }
    } else if (videoElement.mozSrcObject !== void 0) {
      if (videoElement.mozSrcObject !== webRtcStream) {
        videoElement.mozSrcObject = webRtcStream;
      }
    } else {
      videoElement.src = windowMock.URL.createObjectURL(webRtcStream);
    }
  }

  streamBinding.bind = function (mediaElement, webRtcStream, mediaTypes) {
    if (webRtcStream.getTracks().length === 0) {
      logging.debug('Skipping bind of empty webRtcStream');
      return Promise.resolve();
    }

    uncheckedBind(mediaElement, webRtcStream);
    mediaElement.play();

    const cleanupJobs = [];
    const cleanup = () => cleanupJobs.forEach(job => job());
    const addListenerWithCleanup = (obj, evtName, handler) => {
      const wrappedHandler = (evt) => {
        const log = (evtName === 'error' ? logging.error : logging.debug);
        log('streamBinding.bind(', mediaElement, webRtcStream, '):', evtName, ':', evt);
        handler(evt);
      };

      obj.addEventListener(evtName, wrappedHandler);
      cleanupJobs.push(() => obj.removeEventListener(evtName, wrappedHandler));
    };

    const stoppedLoadingError = (type, target, evt) => {
      logging.error('streamBinding.bind:', target, `(${type}) emitted ended event:`, evt);
      return otError(
        errors.HARDWARE_UNAVAILABLE,
        new Error('Stream ended while trying to bind it to a video element.')
      );
    };

    let currReject;
    let listeningForErrors = false;
    const rejectOnErrors = (rej) => {
      currReject = rej;
      if (!listeningForErrors) {
        listeningForErrors = true;

        const applicableTracks = webRtcStream
          .getTracks()
          .filter(track => mediaTypes.indexOf(track.kind) !== -1 && track.enabled);

        applicableTracks.forEach((track) => {
          addListenerWithCleanup(track, 'ended', () => currReject(stoppedLoadingError()));
        });

        addListenerWithCleanup(mediaElement, 'error', event => (
          currReject(videoElementErrorMap(event.target.error))
        ));

        mediaElement.onended = evt => currReject(stoppedLoadingError('tag', mediaElement, evt));
        cleanupJobs.push(() => { mediaElement.onended = null; });
      }
    };

    const load = new Promise((resolve, reject) => {
      rejectOnErrors(reject);
      addListenerWithCleanup(mediaElement, 'loadedmetadata', resolve);
      addListenerWithCleanup(mediaElement, 'timeupdate', resolve);

      if (
        env.name === 'Safari' &&
        mediaTypes.indexOf('video') !== -1 &&
        !streamHasEnabledVideoTrack(webRtcStream)
      ) {
        // For Safari timeupdate and loadedmetadata don't fire if the video track is disabled
        // https://bugs.webkit.org/show_bug.cgi?id=174152
        resolve();
      }
    });

    const getResolutionOrTimeout = () => new Promise((resolve, reject) => {
      rejectOnErrors(reject);

      if (!webRtcStream || webRtcStream.getVideoTracks().length === 0) {
        // If we have no video tracks then there won't be a resolution
        resolve();
        return;
      }

      // We don't want to resolve until we know that the videoWidth and videoHeight are set
      const checkResolution = () => {
        if (mediaElement.videoWidth > 2 && mediaElement.videoHeight > 2) {
          // Chrome does a hack that causes the videoWidth to be 2 when it hasn't completely loaded.
          // Firefox also has videoWidth 0 sometimes, often on first timeupdate. So if there is a
          // video track (even a disabled one - OPENTOK-31836), we make sure videoWidth > 2 before
          // completing.
          resolve();
        }
      };

      checkResolution();

      // Poll for the resolution to see when it's available
      // OPENTOK-34149 - In Chrome for Android timeupdate doesn't continue to
      // fire so we can't use that
      const intervalId = setInterval(checkResolution, POLL_INTERVAL);
      cleanupJobs.push(() => clearInterval(intervalId));

      // Otherwise timeout if it takes longer than 3 seconds
      const timeoutId = setInterval(resolve, TIMEOUT);
      cleanupJobs.push(() => clearTimeout(timeoutId));
    });

    return load
      .then(() => {
        if (mediaTypes.indexOf('video') !== -1) {
          logging.debug('Waiting for video resolution');

          return getResolutionOrTimeout()
            .then(() => {
              const res = `${mediaElement.videoWidth}x${mediaElement.videoHeight}`;
              logging.debug(`Got video resolution: ${res}`);
            });
        }

        return undefined;
      })
      .then(cleanup)
      .then(() =>
        logging.debug('streamBinding.bind(', [mediaElement, webRtcStream, mediaTypes], ') complete')
      )
      .catch((err) => {
        cleanup();
        streamBinding.unbind(mediaElement);
        throw err;
      });
  };

  return streamBinding;
};



// WEBPACK FOOTER //
// ./src/js/helpers/video_element/NativeVideoElementWrapper/streamBinding.js